// Refresh Reminders Card — PSD §9.4 (F9.4). Dashboard.

import type { Alert } from '../../persistence/schema';
import { Tooltip } from '../common/Tooltip';
import { useStore } from '../../store';
import { buildRefreshIcs } from '../../utils/ics';

interface RefreshRemindersCardProps {
  alerts: Alert[];
  onAcknowledge?: ((alertId: string) => void) | undefined;
}

function downloadIcs(entityName: string, valuationDate: string): void {
  const ics = buildRefreshIcs(entityName, valuationDate);
  const blob = new Blob([ics], { type: 'text/calendar' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${entityName.replace(/[^a-z0-9]+/gi, '_')}_409a_refresh.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

export function RefreshRemindersCard({ alerts, onAcknowledge }: RefreshRemindersCardProps) {
  const refreshAlerts = alerts.filter((a) => a.kind === 'refreshDue');

  const activeId = useStore((s) => s.activeValuationId);
  const active = useStore((s) => (activeId ? s.valuations[activeId] ?? null : null));
  const result = useStore((s) => (activeId ? s.resultByValuation[activeId] ?? null : null));

  let cycleNote: string | null = null;
  let canExportIcs = false;
  let entityName = '';
  let valuationDate = '';

  if (result && result.solver.converged) {
    const years = result.headline.safeHarborWindowDays / 365;
    const ttl = result.inputs.assumptions.timeToLiquidity;
    valuationDate = result.inputs.compliance.valuationDate;
    entityName = active?.entityName ?? result.inputs.capTableSnapshot.entityName;
    canExportIcs = valuationDate.length > 0 && entityName.length > 0;
    if (ttl < years) {
      cycleNote = `Valuation covers ${years.toFixed(0)} year(s); at a ${ttl}-year liquidity horizon the valuation outlasts the expected liquidity timeline.`;
    } else {
      const cycles = Math.ceil(ttl / years);
      cycleNote = `Valuation covers ${years.toFixed(0)} year(s); at a ${ttl}-year liquidity horizon, plan for ~${cycles} refresh cycles before exit.`;
    }
  }

  return (
    <div className="card refresh-card">
      <h3>
        Refresh Reminders
        <Tooltip
          label="409A valuations are typically refreshed every 12 months. Alerts fire when the safe-harbor window of a finalized valuation nears expiry."
          ariaLabel="Help: Refresh Reminders"
        />
      </h3>
      {refreshAlerts.length === 0 ? (
        <p className="field-hint">No refresh reminders. All valuations are current.</p>
      ) : (
        <ul>
          {refreshAlerts.map((a) => (
            <li key={a.id} className={`alert-row ${a.acknowledged ? 'acknowledged' : ''}`}>
              <span className={`badge badge-${a.acknowledged ? 'info' : 'warning'}`}>Refresh Due</span>
              <span>{a.message}</span>
              {!a.acknowledged && onAcknowledge && (
                <button className="btn btn-ghost btn-sm" onClick={() => onAcknowledge(a.id)}>
                  Dismiss
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {cycleNote && <p className="field-hint mt-3">{cycleNote}</p>}

      {canExportIcs && (
        <button
          type="button"
          className="btn btn-secondary btn-sm mt-3"
          onClick={() => downloadIcs(entityName, valuationDate)}
        >
          Add to calendar
        </button>
      )}
    </div>
  );
}
